/* eslint-disable react-hooks/exhaustive-deps */
import { gql, useApolloClient, useLazyQuery } from '@apollo/client';
import { Box, Button, Flex, Modal, ModalBody, ModalCloseButton, ModalContent, ModalHeader, ModalOverlay, useToast } from '@chakra-ui/react';
import { faDownload, faFileExcel } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Form, Formik } from 'formik';
import moment from 'moment';
import { useEffect, useMemo, useState } from 'react';
import ReactExport from 'react-export-excel';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';
import c from '../../../constant';
import useMoment from '../../../hooks/useMoment';
import useStore from '../../../store';
import SelectValueFormField from '../../form/SelectValueFormField';

const Q_PARTNERS = gql`
	query GetCompanyAgentPartners($companyId: String) {
		getCompanyAgentPartners(companyId: $companyId) {
			id
			model
			companyType
			company {
				id
				name
				nameKhmer
				type
				logo
			}
			intermediary {
				id
				name
				nameKhmer
				type
				logo
			}
			createdAt
			active
			approvalId
			active
			inactiveAt
			inactiveById
			attachment
		}
	}
`;

const Q_AGENTS = gql`
	query GetIntermediaryProfessionalIndividuals($cursor: CursorInput!, $companyId: String!, $partnerId: String) {
		getIntermediaryProfessionalIndividuals(cursor: $cursor, companyId: $companyId, partnerId: $partnerId) {
			list {
				id
				code
				name
				latinName
				gender
				dob
				validFrom
				expireAt
				agentValidity {
					cardImage
				}
				agentCompany {
					id
					company {
						id
						name
						nameKhmer
						logo
						type
					}
					insuranceAgentCompany {
						id
						name
						nameKhmer
						logo
						type
					}
					startDate
					companyId
					endDate
					deleted
					status
					insuranceAgentCompanyType
				}
			}
			hasNextPage
			nextCursor
		}
	}
`;

const ExcelFile = ReactExport.ExcelFile;
const ExcelSheet = ReactExport.ExcelFile.ExcelSheet;
const ExcelColumn = ReactExport.ExcelFile.ExcelColumn;

function DistributionSystemAgentExportModal({ companyId = null, isOpen = false, onClose = () => null }) {
	const toast = useToast();
	const client = useApolloClient();
	const { toDateString } = useMoment();
	const { t } = useTranslation();
	const { currentLang: lang } = useStore((state) => state.currentLang);
	const [loading, setLoading] = useState(false);
	const [exportedData, setExportedData] = useState([]);
	const [getPartners, { data }] = useLazyQuery(Q_PARTNERS, { fetchPolicy: 'network-only' });
	const validationSchema = useMemo(() => Yup.object().shape({ partnerId: Yup.string() }), []);

	useEffect(loadPartners, [companyId]);

	async function loadPartners() {
		if (companyId) {
			getPartners({ variables: { companyId } });
		}
	}

	function getCode(input) {
		return input?.code;
	}

	function getName(input) {
		return input?.name ?? input?.latinName;
	}

	function getLatinName(input) {
		return input?.latinName ?? input?.name;
	}

	function getDate(input) {
		return toDateString(input, c.dateOnlyFormat);
	}

	function getValid(from, to) {
		return `${getDate(from)} - ${getDate(to)}`;
	}

	function getGender(gender) {
		switch (gender) {
			case 'male':
				return 'ប្រុស';
			case 'female':
				return 'ស្រី';
			default:
				return 'n/a';
		}
	}

	function getType(type) {
		switch (type) {
			case '1':
				return 'ភ្នាក់ងារផ្ទាល់';
			case '2':
				return 'ប៊េងកាសួរេន';
			case '3':
				return 'បុគ្គលវិជ្ជាជីវៈតាមលក្ខខណ្ឌតម្រូវអាជ្ញាបណ្ណ';
			default:
				return 'n/a';
		}
	}

	function getCompanyNameName(input) {
		return input?.nameKhmer ?? input?.name;
	}

	async function getList(values) {
		if (companyId) {
			setLoading(true);
			let list = [];
			let cursor = null;
			let hasNextPage = true;
			while (hasNextPage) {
				const { data, errors } = await client.query({
					query: Q_AGENTS,
					variables: {
						cursor: { limit: 50, cursor },
						companyId,
						partnerId: values?.partnerId ? values?.partnerId : null,
					},
					fetchPolicy: 'network-only',
				});
				if (data && !errors) {
					const result = data?.getIntermediaryProfessionalIndividuals;
					list.push(...result?.list);
					cursor = result?.nextCursor;
					hasNextPage = result?.hasNextPage;
				} else {
					cursor = null;
					hasNextPage = false;
					errors?.map(({ message }, i) =>
						toast({
							title: t(message),
							status: 'error',
							isClosable: true,
							position: 'bottom-right',
						}),
					);
				}
			}
			setLoading(false);
			setExportedData(
				list?.map((e, i) => ({
					number: i + 1,
					code: getCode(e),
					name: getName(e),
					nameLatin: getLatinName(e),
					gender: getGender(e?.gender),
					dob: getDate(e?.dob),
					insuranceCompany: getCompanyNameName(e?.agentCompany?.company),
					insuranceAgentCompany: getCompanyNameName(e?.agentCompany?.insuranceAgentCompany),
					startWorkingDate: getDate(e?.agentCompany?.startDate),
					valid: getValid(e?.validFrom, e?.expireAt),
					type: getType(e?.agentCompany.insuranceAgentCompanyType),
				})),
			);
		}
	}

	return (
		<>
			<Modal borderRadius={c.borderRadius} isOpen={isOpen} onClose={onClose} closeOnEsc={!loading} closeOnOverlayClick={!loading}>
				<ModalOverlay />
				<ModalContent borderRadius={c.borderRadius}>
					<ModalHeader>{t('Export To Excel')}</ModalHeader>
					<ModalCloseButton isDisabled={loading} />
					<ModalBody>
						<Formik validationSchema={validationSchema} initialValues={{ partnerId: '' }} onSubmit={getList}>
							{(props) => (
								<Box pb="4">
									<Form>
										{false === data?.getCompanyAgentPartners?.map((e) => e?.company).some((e) => e?.id === companyId) && (
											<SelectValueFormField
												{...props}
												placeholder={t('Select')}
												name="partnerId"
												required={false}
												label="Partner"
												options={data?.getCompanyAgentPartners
													?.map((e) => e?.company)
													?.map((e) => ({ label: lang === 'kh' ? e?.nameKhmer : e?.name, value: e?.id }))}
											/>
										)}
										<Flex
											mt={data?.getCompanyAgentPartners?.map((e) => e?.company).some((e) => e?.id === companyId) ? 0 : 6}
											alignItems="center"
											justifyContent="flex-start"
										>
											<Button
												id="download-btn"
												leftIcon={<FontAwesomeIcon icon={faDownload} style={{ fontSize: 16 }} />}
												variant="solid"
												size="md"
												h="42px"
												w="158px"
												type="submit"
												borderRadius={c.borderRadius}
												color="#3498db"
												border="1px solid #3498db33"
												isLoading={loading}
											>
												{t('Get Data')}
											</Button>
											<ExcelFile
												filename={`Insurance Agent - Generated At ${moment().format('DD MM YYYY hh mm ss a')}`}
												element={
													<Button
														id="export-btn"
														leftIcon={<FontAwesomeIcon icon={faFileExcel} style={{ fontSize: 16 }} />}
														variant="solid"
														size="md"
														h="42px"
														w="158px"
														borderRadius={c.borderRadius}
														color="#38A169"
														border="1px solid #38A16933"
														isDisabled={loading ? true : exportedData?.length > 0 ? false : true}
														ml={2}
													>
														{t('Export')}
													</Button>
												}
											>
												<ExcelSheet data={exportedData} name={`តារាង`}>
													<ExcelColumn label="ល.រ" value="number" />
													<ExcelColumn label="អត្តលេខបណ្ណចុះបញ្ជី" value="code" />
													<ExcelColumn label="ឈ្មោះ" value="name" />
													<ExcelColumn label="ឈ្មោះជាភាសាឡាតាំង" value="nameLatin" />
													<ExcelColumn label="ភេទ" value="gender" />
													<ExcelColumn label="ថ្ងៃខែឆ្នាំកំណើត" value="dob" />
													<ExcelColumn label="ក្រុមហ៊ុនធានារ៉ាប់រងដៃគូ" value="insuranceCompany" />
													<ExcelColumn label="ក្រុមហ៊ុនភ្នាក់ងារធានារ៉ាប់រង" value="insuranceAgentCompany" />
													<ExcelColumn label="កាលបរិច្ឆេទចាប់ផ្តើមការងារ" value="startWorkingDate" />
													<ExcelColumn label="សុពលភាព" value="valid" />
													<ExcelColumn label="ទម្រង់ភ្នាក់ងារ" value="type" />
												</ExcelSheet>
											</ExcelFile>
										</Flex>
									</Form>
								</Box>
							)}
						</Formik>
					</ModalBody>
				</ModalContent>
			</Modal>
		</>
	);
}

export default DistributionSystemAgentExportModal;
